/* eslint-disable eqeqeq */
import {
  CommentOutlined,
  DownOutlined,
  SmileOutlined,
} from "@ant-design/icons";
import {
  Avatar,
  Button,
  Card,
  Col,
  DatePicker,
  Dropdown,
  List,
  Menu,
  message,
  Modal,
  Row,
  Space,
  Spin,
  Statistic,
  Table,
  Tooltip,
} from "antd";
import moment from "moment";
import React, { useEffect, useState } from "react";
import Emoji from "react-emoji-render";

import {
  getAverageRepliesChannel,
  getMessageType,
  getPopularPosts,
  getPowerUsers,
  getTTFRChannel,
} from "../../lib/queries";
import { getSupaClient } from "../../lib/supabase";
import RenderLineChart from "../Charts/RenderLineChart";
import RenderPieChart from "../Charts/RenderPieChart";
import { useEndTime, useStartTime } from "../DateContext/DateContext";
import UserListItem from "../UserListItem/UserListItem";

const ChannelDetail = (props) => {
  const supabase = getSupaClient();
  const teamId = localStorage.getItem("team-id");
  const [loading, setLoading] = useState(true);
  const { startTime } = useStartTime();
  const { endTime } = useEndTime();
  const [popularPosts, setPopularPosts] = useState([]);
  const [powerUsers, setPowerUsers] = useState([]);
  const [messageType, setMessageType] = useState([]);
  const [averageReplies, setAverageReplies] = useState([]);
  const [channelN, setChannelN] = useState([]);
  const [ttfr, setTtfr] = useState([]);
  const [isPopularModalVisible, setPopularModalVisible] = useState(false);
  const [threadCount, setThreadCount] = useState(null);
  const [parentCount, setParentCount] = useState(null);
  const [reactionCount, setReactionCount] = useState(null);
  window.moment = moment;

  const mapResults = async (results) => {
    results = await results.map((result) => {
      result.key = result.slack_id;
      result.channelName = `#${result.channel.name}`;
      result.userAdmin = result.slack_user ? result.slack_user.is_admin : "";
      return result;
    });
    return results;
  };

  const loadPopular = async (start, end) => {
    const data = await getPopularPosts(
      start,
      end,
      teamId,
      props.channelId ? props.channelId : props.match?.params?.channelId
    );
    if (data) {
      const displayResults = await mapResults(data);
      setPopularPosts(displayResults);
    }
    return data;
  };

  const loadTtfr = async (start, end) => {
    if (props.channelId || props.match?.params?.channelId) {
      const ttfrResult = await getTTFRChannel(
        start,
        end,
        props.channelId ? props.channelId : props.match?.params?.channelId
      );
      if (ttfrResult.data) {
        // setup the average line chart array based on average array received
        const results = ttfrResult.data.map((item) => {
          return {
            ...item,
            current_avg: ttfrResult.average[0].average,
            previous_avg: ttfrResult.average[1].average,
          };
        });
        setTtfr({ results });
      }
      return ttfrResult;
    }
    return null;
  };

  const loadAverageReplies = async (start, end) => {
    const avgReplies = await getAverageRepliesChannel(
      start,
      end,
      teamId,
      props.channelId ? props.channelId : props.match?.params?.channelId
    );
    if (avgReplies.average) {
      const results = avgReplies.data.map((item) => {
        return {
          ...item,
          current_avg: avgReplies.average[0].average,
          previous_avg: avgReplies.average[1].average,
        };
      });
      setAverageReplies({ results });
    }
    return avgReplies;
  };

  const loadMessageType = async (start, end) => {
    const data = await getMessageType(
      start,
      end,
      teamId,
      props.channelId ? props.channelId : props.match?.params?.channelId
    );
    if (data) {
      const formattedResult = data.map(function (val) {
        if (val.is_admin === "false") {
          val.name = "community";
        } else {
          val.name = "moderators";
        }
        return val;
      });
      setMessageType(formattedResult);
    }
    return data;
  };

  const loadPowerUsers = async (start, end) => {
    const data = await getPowerUsers(
      start,
      end,
      teamId,
      props.channelId ? props.channelId : props.match?.params?.channelId
    );
    if (data) {
      setPowerUsers(data);
    }
    return data;
  };

  const loadChannelName = async () => {
    const channelRes = await supabase
      .from("channels")
      .select()
      .filter(
        "slack_id",
        "eq",
        props.channelId ? props.channelId : props.match?.params?.channelId
      );
    setChannelN(channelRes.data[0]?.name);
  };

  const loadChannelParentMessageCount = async (start, end) => {
    const { count } = await supabase
      .from("parent_msgs")
      .select("*", { count: "exact" })
      .filter(
        "channel",
        "eq",
        props.channelId ? props.channelId : props.match?.params?.channelId
      )
      .lte("formatted_ts", end)
      .gte("formatted_ts", start);
    setParentCount(count);
  };

  const loadChannelThreadMessageCount = async (start, end) => {
    const { count } = await supabase
      .from("thread_msgs")
      .select("*", { count: "exact" })
      .filter(
        "channel",
        "eq",
        props.channelId ? props.channelId : props.match?.params?.channelId
      )
      .lte("formatted_ts", end)
      .gte("formatted_ts", start);
    setThreadCount(count);
  };

  const loadChannelReactionsCount = async (start, end) => {
    const { count } = await supabase
      .from("msg_reactions")
      .select("*", { count: "exact" })
      .filter(
        "channel",
        "eq",
        props.channelId ? props.channelId : props.match?.params?.channelId
      )
      .lte("formatted_ts", end)
      .gte("formatted_ts", start);
    setReactionCount(count);
  };

  const loadPageData = async (start, end) => {
    loadAverageReplies(start, end);
    loadPopular(start, end);
    loadTtfr(start, end);
    loadPowerUsers(start, end);
    loadMessageType(start, end);
    setLoading(false);
    loadChannelParentMessageCount(start, end);
    loadChannelThreadMessageCount(start, end);
    loadChannelReactionsCount(start, end);
  };

  useEffect(() => {
    setLoading(true);
    loadChannelName();
    loadPageData(startTime, endTime);

    document.title = "Channel Detail | Dots";
  }, [props.channelId, props.match?.params?.channelId, endTime, startTime]);

  const showPopularModal = () => {
    setPopularModalVisible(true);
  };

  const hidePopularModal = () => {
    setPopularModalVisible(false);
  };

  const getChannelFilters = (posts) => {
    const filterValues = new Set();
    // eslint-disable-next-line no-restricted-syntax
    for (const post of posts) {
      filterValues.add(post.channelName);
    }
    const filters = [];
    // eslint-disable-next-line no-restricted-syntax
    for (const value of filterValues) {
      filters.push({ text: value, value });
    }
    return filters;
  };

  const popularMessagesColumns = [
    {
      title: "Message",
      dataIndex: "text",
      key: "text",
      render: (text, record) => (
        <div>
          <Row>
            <Col span={2}>
              {record.slack_user ? (
                <Avatar src={record.slack_user.image_32} />
              ) : null}
            </Col>
            <Col span={22}>
              <Row>
                <span style={{ color: "grey", marginRight: "10px" }}>
                  {record.slack_user.name}
                </span>
                <Tooltip title="Click to open in slack">
                  <a href={record.permalink} target="_blank" rel="noreferrer">
                    <img
                      alt="Link"
                      width="12"
                      src="/external-link-alt-solid.svg"
                    />
                  </a>
                </Tooltip>
              </Row>
              <Row>
                <Emoji text={record.text} />
              </Row>
              <Row>
                <span style={{ color: "grey", marginRight: "10px" }}>
                  {record.reaction_count} reactions | {record.reply_count}{" "}
                  replies{" "}
                </span>
              </Row>
            </Col>
          </Row>
        </div>
      ),
      filters: [
        { text: "Admin", value: true },
        { text: "Non-admin", value: false },
      ],
      onFilter: (value, record) => record.userAdmin === value,
    },
    {
      title: "Channel",
      dataIndex: "channelName",
      filters: getChannelFilters(popularPosts),
      onFilter: (value, record) => record.channelName.indexOf(value) === 0,
      sorter: (a, b) => {
        if (a.channelName < b.channelName) {
          return -1;
        }
        if (a.channelName > b.channelName) {
          return 1;
        }
        return 0;
      },
      sortDirections: ["descend", "ascend"],
      width: "20%",
    },
  ];

  return (
    <div>
      <Spin spinning={loading}>
        <Row justify="spaced-around" gutter={[32, 32]}>
          <Col lg={12} md={24}>
            <Card
              title="Popular threads"
              bordered={false}
              extra={
                // eslint-disable-next-line jsx-a11y/anchor-is-valid
                <a href="#" onClick={showPopularModal}>
                  View more
                </a>
              }
            >
              <List
                className="popular-threads"
                dataSource={popularPosts}
                renderItem={(item) => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={
                        <Avatar
                          src={
                            item.slack_user
                              ? item.slack_user.image_32
                              : "/avatar_slack.png"
                          }
                        />
                      }
                      title={
                        <p>
                          <p>{item.slack_user ? item.slack_user.name : ""}</p>{" "}
                        </p>
                      }
                      description={
                        <a
                          href={item.permalink}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <Emoji text={item.text} />
                        </a>
                      }
                    />
                    <div className="popular-thread-replies">
                      <p>
                        {item.reply_count} <CommentOutlined />{" "}
                      </p>
                      <p>
                        {item.reaction_count} <SmileOutlined />{" "}
                      </p>
                    </div>
                  </List.Item>
                )}
              />
            </Card>
          </Col>

          <Col lg={12} md={24}>
            <Card bordered={false} className="card-engagement">
              <Row gutter={[16, 16]}>
                <Col lg={12} sm={12}>
                  <Statistic
                    title="Total messages"
                    value={
                      parentCount && threadCount
                        ? parentCount + threadCount
                        : "-"
                    }
                  />
                </Col>
                <Col lg={12} sm={12}>
                  <Statistic title="# of threads" value={parentCount || "-"} />
                </Col>
                <Col lg={12} sm={12}>
                  <Statistic
                    title="# of replies to threads"
                    value={threadCount || "-"}
                  />
                </Col>
                <Col lg={12} sm={12}>
                  <Statistic
                    title="Reactions added"
                    value={reactionCount || "-"}
                  />
                </Col>
              </Row>
            </Card>
            <Card
              title={`Most active members in #${channelN}`}
              bordered={false}
              extra={[]}
              style={{ marginTop: 40 }}
            >
              <List
                dataSource={powerUsers}
                renderItem={(item) => <UserListItem user={item} />}
              />
            </Card>
          </Col>
          <Col lg={12} md={24}>
            <Card
              title="# conversations by user type"
              bordered={false}
              extra={[]}
            >
              <div style={{ width: "100%", height: "250px" }}>
                <RenderPieChart
                  data={messageType}
                  dataline="avg_reply_count"
                  dataline_name="Avg reply count"
                />
              </div>
            </Card>
          </Col>

          <Col lg={12} md={24}>
            <Card title="Time to first reply (hrs)" bordered={false} extra={[]}>
              <div style={{ width: "100%", height: "250px" }}>
                <RenderLineChart
                  data={ttfr.results}
                  dataline="avg_ttfr"
                  average
                  dataline_name="Time to first reply"
                />
              </div>
            </Card>
          </Col>

          <Col lg={12} md={24}>
            <Card
              title="Average replies (per thread)"
              bordered={false}
              extra={[]}
            >
              <div style={{ width: "100%", height: "250px" }}>
                <RenderLineChart
                  data={averageReplies.results}
                  dataline="avg_reply_count"
                  average
                  dataline_name="Avg reply count"
                />
              </div>
            </Card>
          </Col>
        </Row>

        <Modal
          width={1000}
          title="Popular messages"
          style={{ top: 40, right: 10 }}
          visible={isPopularModalVisible}
          onCancel={hidePopularModal}
          footer={[]}
        >
          <Row justify="spaced-around">
            <div style={{ width: "100%", height: "100%" }}>
              <Table
                dataSource={popularPosts}
                columns={popularMessagesColumns}
                style={{}}
              />
            </div>
          </Row>
        </Modal>
      </Spin>
    </div>
  );
};

export default ChannelDetail;
